import { useEffect, useId, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { filterBankOptions, type BankOption } from "../bank-options";

/** Registration only; stored labels remain freely editable in AccountForm. */
export function BankSelector({
  value,
  onChange,
}: {
  value: string;
  onChange: (name: string) => void;
}) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState(value);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const options = filterBankOptions(query);
  const activeOption = open && active >= 0 ? options[active] : undefined;
  const optionId = (option: BankOption) =>
    `${id}-${option.country}-${option.code}`;
  useEffect(() => {
    if (activeOption)
      document
        .getElementById(optionId(activeOption))
        ?.scrollIntoView({ block: "nearest" });
  });
  function select(option: BankOption) {
    setQuery(option.name);
    onChange(option.name);
    setOpen(false);
    setActive(-1);
    input.current?.focus();
  }
  return (
    <div
      className="relative space-y-2"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setOpen(false);
          setActive(-1);
        }
      }}
    >
      <Label htmlFor={id}>Banco</Label>
      <div className="flex gap-1">
        <Input
          ref={input}
          id={id}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={`${id}-options`}
          aria-describedby={`${id}-hint`}
          aria-activedescendant={
            activeOption ? optionId(activeOption) : undefined
          }
          autoComplete="off"
          required
          maxLength={4096}
          placeholder="Buscar banco"
          value={query}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onChange={(event) => {
            setQuery(event.target.value);
            onChange("");
            setActive(-1);
            setOpen(true);
          }}
          onKeyDown={(event) => {
            if (event.nativeEvent.isComposing) return;
            if (event.key === "ArrowDown" || event.key === "ArrowUp") {
              event.preventDefault();
              setOpen(true);
              setActive((current) =>
                event.key === "ArrowDown"
                  ? Math.min(open ? current + 1 : 0, options.length - 1)
                  : Math.max(
                      open && current >= 0 ? current - 1 : options.length - 1,
                      0,
                    ),
              );
            } else if (event.key === "Enter" && open) {
              event.preventDefault();
              if (activeOption) select(activeOption);
            } else if (event.key === "Escape") {
              event.preventDefault();
              setOpen(false);
              setActive(-1);
            }
          }}
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label="Mostrar bancos"
          aria-expanded={open}
          aria-controls={`${id}-options`}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            input.current?.focus();
            setOpen(!open);
            setActive(-1);
          }}
        >
          <ChevronDown className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>
      <p id={`${id}-hint`} className="text-sm text-muted-foreground">
        Escribe para buscar y selecciona un banco de la lista.
      </p>
      {open && (
        <div className="absolute z-20 w-full rounded-md border bg-popover text-popover-foreground shadow-md">
          <ul
            id={`${id}-options`}
            role="listbox"
            aria-label="Bancos"
            className="max-h-60 overflow-y-auto p-1"
          >
            {options.map((option, index) => (
              <li
                key={`${option.country}-${option.code}`}
                id={optionId(option)}
                role="option"
                aria-selected={value === option.name}
                className={`cursor-pointer rounded px-3 py-2 text-sm ${active === index ? "bg-accent text-accent-foreground" : "hover:bg-accent"}`}
                onMouseDown={(event) => event.preventDefault()}
                onMouseMove={() => setActive(index)}
                onClick={() => select(option)}
              >
                {option.name}{" "}
                <span className="text-muted-foreground">({option.code})</span>
              </li>
            ))}
          </ul>
          {options.length === 0 && (
            <p role="status" className="p-3 text-sm">
              Sin bancos coincidentes.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
